/**
 * "Live now" across the projects shared with the person (`GET /api/v1/live`, services/CollabService.js), one poll for
 * the whole page: Home's LiveNow, the sidebar's LIVE NOW list and the kanban's live bars all read the same answer.
 * Polled every 20 s while anyone is subscribed and the page is visible (the gateway only asks awake sandboxes, so this
 * never wakes one). `null` until the first answer, or where the endpoint does not exist.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { getLiveNow } from '../services/CollabService';

export const LIVE_POLL_MS = 20000;

const store = {
  answer: null,
  listeners: new Set(),
  answerListeners: new Set(),
  timer: null,
  inFlight: null,
};

function emit() {
  store.listeners.forEach((listener) => listener());
}

/** Loads now (one request at a time); resolves to the answer. */
export function refreshLiveNow() {
  if (store.inFlight) { return store.inFlight; }
  store.inFlight = getLiveNow()
    .then((answer) => {
      const previous = store.answer;
      store.answer = answer;
      store.answerListeners.forEach((listener) => {
        try { listener(answer, previous); } catch { /* a listener's own problem */ }
      });
      emit();
      return answer;
    })
    .catch(() => store.answer) // keep the last answer
    .finally(() => { store.inFlight = null; });
  return store.inFlight;
}

function start() {
  if (store.timer) { return; }
  refreshLiveNow();
  store.timer = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { refreshLiveNow(); }
  }, LIVE_POLL_MS);
}

function stop() {
  if (store.listeners.size > 0 || store.answerListeners.size > 0 || !store.timer) { return; }
  clearInterval(store.timer);
  store.timer = null;
}

function subscribe(listener) {
  store.listeners.add(listener);
  start();
  return () => {
    store.listeners.delete(listener);
    stop();
  };
}

/** Called with `(answer, previous)` after every poll; keeps the poll running while subscribed. */
export function onLiveAnswer(listener) {
  store.answerListeners.add(listener);
  start();
  return () => {
    store.answerListeners.delete(listener);
    stop();
  };
}

const snapshot = () => store.answer;

/** The latest `{ projects }` answer, or null. `enabled` false: nothing is polled for this caller. */
export default function useLiveNow({ enabled = true } = {}) {
  const answer = useSyncExternalStore(enabled ? subscribe : noopSubscribe, snapshot, snapshot);
  return enabled ? answer : null;
}

const noopSubscribe = () => () => {};

/** Runs the latest `listener(answer, previous)` after every poll while mounted (subscribed once). */
export function useLiveAnswer(listener) {
  const ref = useRef(listener);
  ref.current = listener;
  useEffect(() => onLiveAnswer((answer, previous) => ref.current(answer, previous)), []);
}

/** Tests only: forget the shared answer and stop polling. */
export function resetLiveNowForTests() {
  if (store.timer) { clearInterval(store.timer); }
  store.timer = null;
  store.answer = null;
  store.inFlight = null;
  store.listeners.clear();
  store.answerListeners.clear();
}
